import Card from "@rio-cloud/rio-uikit/Card";
import ContentLoader from "@rio-cloud/rio-uikit/ContentLoader";

interface Top5VehiclesTableSkeletonProps {
  rows?: number;
  className?: string;
}

const TITLE_LINE_HEIGHT = 23;
const SUBTITLE_LINE_HEIGHT = 23;
const FILTER_ICON_SIZE = 18;
const FILTER_ICON_BOX_SIZE = 24;
const HEADER_CELL_LINE_HEIGHT = 16;
const BODY_CELL_LINE_HEIGHT = 16;

const SUBTITLE_STYLE = {
  fontFamily: "Source Sans Pro",
  fontWeight: 300,
  fontStyle: "italic",
  fontSize: 14,
  lineHeight: "22.86px",
  letterSpacing: 0,
  verticalAlign: "middle",
} as const;

const FILTER_BUTTON_STYLE = {
  backgroundColor: "transparent",
  border: "none",
  padding: 4,
} as const;

const FILTER_ICON_STYLE = {
  display: "inline-grid",
  placeItems: "center",
  verticalAlign: "middle",
  width: FILTER_ICON_BOX_SIZE,
  height: FILTER_ICON_BOX_SIZE,
  fontSize: FILTER_ICON_SIZE,
  lineHeight: 1,
} as const;

const HEADER_CELL_STYLE = { fontWeight: 500, fontSize: 13 } as const;
const HEADER_CELL_NOWRAP_STYLE = { fontWeight: 500, fontSize: 13, whiteSpace: "nowrap" } as const;
const BODY_CELL_STYLE = { fontSize: 13 } as const;
const BODY_CELL_NOWRAP_STYLE = { fontSize: 13, whiteSpace: "nowrap" } as const;

const HEADER_WIDTHS = [70, 55, 125, 120];

const PLATE_WIDTH = 66;
const FUEL_WIDTHS = [58, 70, 50, 64, 56];
const CONSUMPTION_UNIT_WIDTHS = [74, 86, 66, 80, 70];
const CONSUMPTION_CURRENCY_WIDTHS = [78, 90, 70, 84, 74];

const DEFAULT_CARD_CLASS_NAME = "padding-y-25 padding-x-15 padding-x-25-sm";

export default function Top5VehiclesTableSkeleton({
  rows = 5,
  className = DEFAULT_CARD_CLASS_NAME,
}: Readonly<Top5VehiclesTableSkeletonProps>) {
  const vehicles = Array.from({ length: Math.max(0, rows) }, (_, index) => ({
    key: `top5-vehicles-skeleton-row-${index}`,
    fuelWidth: FUEL_WIDTHS[index % FUEL_WIDTHS.length],
    consumptionUnitWidth: CONSUMPTION_UNIT_WIDTHS[index % CONSUMPTION_UNIT_WIDTHS.length],
    consumptionCurrencyWidth: CONSUMPTION_CURRENCY_WIDTHS[index % CONSUMPTION_CURRENCY_WIDTHS.length],
  }));

  return (
    <Card className={className}>
      <div className="display-flex flex-wrap justify-content-between align-items-center padding-bottom-20">
        <div className="display-flex flex-wrap align-items-center gap-10 min-width-0">
          <span className="text-size-16 text-bold">
            <ContentLoader className="display-block" height={TITLE_LINE_HEIGHT} width={200} />
          </span>
          <span style={SUBTITLE_STYLE}>
            <ContentLoader className="display-block" height={SUBTITLE_LINE_HEIGHT} width={120} />
          </span>
        </div>
        <div style={{ position: "relative" }}>
          <span className="btn btn-sm" style={FILTER_BUTTON_STYLE}>
            <span style={FILTER_ICON_STYLE}>
              <ContentLoader className="display-block" height={FILTER_ICON_SIZE} width={FILTER_ICON_SIZE} />
            </span>
          </span>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-hover" style={{ marginBottom: 0 }}>
          <thead>
            <tr className="bg-lighter">
              <th className="text-color-gray" style={HEADER_CELL_NOWRAP_STYLE}>
                <ContentLoader className="display-block" height={HEADER_CELL_LINE_HEIGHT} width={HEADER_WIDTHS[0]} />
              </th>
              <th className="text-color-gray" style={HEADER_CELL_STYLE}>
                <ContentLoader className="display-block" height={HEADER_CELL_LINE_HEIGHT} width={HEADER_WIDTHS[1]} />
              </th>
              <th className="text-color-gray" style={HEADER_CELL_STYLE}>
                <ContentLoader className="display-block" height={HEADER_CELL_LINE_HEIGHT} width={HEADER_WIDTHS[2]} />
              </th>
              <th className="text-color-gray" style={HEADER_CELL_STYLE}>
                <ContentLoader className="display-block" height={HEADER_CELL_LINE_HEIGHT} width={HEADER_WIDTHS[3]} />
              </th>
            </tr>
          </thead>
          <tbody>
            {vehicles.map((vehicle) => (
              <tr key={vehicle.key}>
                <td style={BODY_CELL_NOWRAP_STYLE}>
                  <ContentLoader className="display-block" height={BODY_CELL_LINE_HEIGHT} width={PLATE_WIDTH} />
                </td>
                <td style={BODY_CELL_STYLE}>
                  <ContentLoader className="display-block" height={BODY_CELL_LINE_HEIGHT} width={vehicle.fuelWidth} />
                </td>
                <td style={BODY_CELL_STYLE}>
                  <ContentLoader
                    className="display-block"
                    height={BODY_CELL_LINE_HEIGHT}
                    width={vehicle.consumptionUnitWidth}
                  />
                </td>
                <td style={BODY_CELL_STYLE}>
                  <ContentLoader
                    className="display-block"
                    height={BODY_CELL_LINE_HEIGHT}
                    width={vehicle.consumptionCurrencyWidth}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
